import {uuid} from 'uuidv4';
import HasOperations from "utils/HasOperations.js";
import {effectOperations} from './EffectOperations.js';
import {getTrackShortName} from 'utils/getTrackShortName.js';
import Tone from 'tone';
import {MASTER, SENDS_ONLY} from 'constants/track-output-types.js';
import SliderRender from './SliderRender.js';
import {PAD} from 'constants/object-types.js';
import MidiTrigger from "effects/midi/MidiTrigger.js";
import {CHOKE, TRIGGER_ROUTING} from "messaging/message-types.js";
import RackUtils from "effects/rack/RackUtils.js";
import {
    setEffectCollapsed,
    setPads, setPadSlots, setPadFXMode} from 'actions/index.js';
import Dispatchable from "utils/Dispatchable.js";
import Racked from './Racked.js';
import NodeMessaging from "messaging/NodeMessaging.js"
import ShaderBackground from "zobject/custom/ShaderBackground.js";

const ANIMATION_LOOKAHEAD = 10;

export const MIN_VOLUME = -40;
export const MAX_VOLUME = 0;

const NUM_SLOTS = 9;

window.ROOTY = 3;

class Pad extends HasOperations(SliderRender(ShaderBackground(NodeMessaging(Racked(RackUtils(Dispatchable(class {}))))))) {
    constructor(subSequence, createRack=true) {
        super();
        this.subSequence = subSequence;
        this.sequencerEngine = subSequence.sequencerEngine;
        this.id = uuid();
        this.sequencerEngine.idToObject[this.id] = this;
        this.tracks = new Array(NUM_SLOTS).fill(false);
        if (createRack) {
            this.createEffectRack();
        }

        this.newInlet(TRIGGER_ROUTING, (msg) => this.onTrigger(msg));
        this.newInlet(CHOKE, (msg) => this.onChoke(msg));
        //this.newInlet("slots", (msg) => this.onSlots(msg));
        // this.newInlet("sliders material", (msg) => this.onSliderMaterial(msg));

        this.newInlet("slider values", (msg) => this.onSliderValues(msg));
        this.newInlet("add effect", ([msg]) => this.onAddEffectMessage(msg));

        this.registerOperations("effect", effectOperations);
        this.newInlet("effect operation", ([msg]) => this.onOperation("effect", msg));

        // at the end of each step scheduling, we send out a list of true/false values
        // for whether each pad is on/off
        this.newOutlet("pads playing");
        this.newOutlet("notes playing");
        this.newOutlet("size");

        // sends the "main tag" for each sample loaded in the pad
        // this.newOutlet("track types");
        this.newOutlet("slider levels");
        this.newOutlet("mouse downs");
        this.newOutlet("effect added");

        this.isPad = true;
        this.initShaderBackground();
    }

    onChangePadsCount(x) {
        if (isNaN(x)) {
            return;
        }
        if (x > this.tracks.length) {
            let arr = new Array(x - this.tracks.length).fill(false);
            this.tracks = [... this.tracks, ...arr];
        } else {
            for (let i=x; i < this.tracks.length; i++) {
                if (this.tracks[i]) {
                    this.removeTrack(this.tracks[i]);
                }
            }
            this.tracks = this.tracks.slice(0, x);
        }
    }

    set padFXMode(mode) {
        for (let track of this.tracks) {
            if (!track) {
                continue;
            }
            track.outputTypeParameter.set(mode);
        }
        this.dispatch(
            setPadFXMode(this.id, mode));
    }

    getObjectType() {
        return PAD;
    }

    onSlots(msg) {
        msg = msg[0];
        this.slotsMapping = msg;
    }

    onTrigger(msg) {
        msg = msg[0];
        if (!msg || !msg[1]) {
            return;
        }
        for (let trigger of msg[1]) {
            let slotNumber = trigger.outputNumber-1;
            if (this.slotsMapping && this.slotsMapping[slotNumber] !== undefined) {
                slotNumber = this.slotsMapping[slotNumber];
            }
            let track = this.slots[slotNumber];
            if (track) {
                trigger = {
                    ... trigger
                };
                trigger.outputNumber = 0;
                let {stepData, time, stepNumber} = trigger;
                stepData = track.midiEffects.getStep(stepData);
                stepData.track = track;
                track.midiEffects.input(stepData, time, stepNumber);
                track.midiEffects.receivedTriggers = track.midiEffects.inputedTriggers;
            }
        }
    }

    onChoke(msg) {
        msg = parseFloat(msg[0]);

        if (!isNaN(msg)) {
            this.choking = msg*2;
        }
    }

    onNewStep() {
        this.choking--;
    }

    get slots() {
        return this.tracks;
    }

    containsSample(sample) {
        return this.tracks.filter(
            x => !x.isFrozen)
            .find(
            x => x && x.sample === sample);
    }
    
    switch(a,b) {
        let tmpA = this.tracks[a];
        let tmpB = this.tracks[b];
        this.tracks[a] = tmpB;
        this.tracks[b] = tmpA;
        this.updateRedux();
    }


    removeTrack(track) {
        for (let i in this.tracks) {
            if (this.tracks[i] === track) {
                this.tracks[i] = false;
            }
        }
        track.pad = undefined;
        this.updateRedux();
        this.onTrackRemoved(track);
    }

    get slotTracks() {
        return this.tracks.filter(x => x);
    }

    get patternLength() {
        return Math.max(
            ... this.slotTracks.filter(
                x => x.pattern)
                .map(x => x.pattern.length()));
    }

    get padFXMode() {
        return this.state.pads.padFXMode[this.id] || MASTER;
    }

    addTrack(track, slotNumber, newTrack=false) {
        if (!track) {
            return;
        }
        track.changeOutputType(this.padFXMode);

        if (this.slots[slotNumber]) {
            // theres already a track here so we remove that track
            // from the pad
            this.deleteTrack(this.slots[slotNumber]);
        }
        let tracks = [... this.tracks];
        tracks[slotNumber] = track;
        this.tracks = tracks;
        if (newTrack) {
            // track.pattern.changeLength(this.patternLength);
        }
        if (track.pad) {
            // already has a pad then we need remove the track from there
            track.pad.removeTrack(track);
        }
        track.pad = this;
        this.updateRedux();
        this.onTrackAdded(track);
        this.sendTags();
    }

    async newSample(sample, slotNumber) {
        let track = await this.sequencerEngine.addSound(
            sample, undefined, undefined, undefined, undefined, undefined, false);
        this.addTrack(track, slotNumber);
        return track;
    }

    updateRedux() {
        this.tracks = [... this.tracks];
        this.dispatch(setPadSlots(
            this.id, this.tracks));
    }

    deleteTrack(track) {
        let indexOf = this.slots.indexOf(track);
        if (indexOf === -1) {
            return;
        }
        this.slots[indexOf] = false;
        track.pad = false;
        this.updateRedux();
    }

    getJson() {
        return {
            id: this.id,
            tracks: this.tracks.map(x => x === false ? x : x.trackNumber),
            x: this.x,
            name: this.name,
            y: this.y,
            padFXMode: this.padFXMode,
            fxMacroValue: this.fxMacro.macroParameter.get(true),
            fxMacro: this.fxMacro.macroParameter.getJson(),
        };
    }

    getParameters() {
        return this.getAllParameters();
    }

    getAllParameters() {
        if (!this.fxMacro || !this.effectRack) {
            return [];
        }
        return [
            this.fxMacro.macroParameter,
            ... this.effectRack.getParameters()
        ];
    }

    getJsonForPack() {
        let content = [];
        for (let slot of this.tracks) {
            if (!slot) {
                content.push(false);
            } else {
                if (slot.sample) {
                    content.push({
                        type: "SAMPLE",
                        contentHash: slot.sample.getId(),
                        preset: this.sequencerEngine.getTrackPreset(
                            slot.trackNumber)
                    });
                } else if (slot.preset && slot.preset.contentHash) {
                    content.push({
                        type: "PRESET",
                        contentHash: slot.preset.contentHash,
                        preset: this.sequencerEngine.getTrackPreset(
                            slot.trackNumber)
                    });
                } else {
                    content.push(false);
                }
            }
        }
        content.fx = this.effectRack.getJson();

        return content;
    }

    fromJson(json) {
        if (json.id) {
            this.id = json.id;
            this.sequencerEngine.idToObject[this.id] = this;
        }
        let i=0;
        let array = [];
        if (Array.isArray(json)) {
            array = json;
        } else {
            array = json.tracks;
            this.x = json.x;
            this.y = json.y;
        }

        if (json.fxMacroValue === undefined) {
            // legacy shit (we hadn't created effect racks yet)
            this.createEffectRack();
        } else {
            this.linkEffectRackContext();
        }

        for (let q=0; q< array.length; q++) {
            let trackNumber= array[q];
            if (trackNumber !== false) {
                let track = this.subSequence.tracks[parseFloat(trackNumber)];
                this.addTrack(track, i);
            }
            i++;
        }
        this.fxMacroValue = json.fxMacroValue;
        this.fxMacroJson = json.fxMacro;

        if (json.fx) {
            this.effectRack.fromJson(json.fx);
        }

        if (json.padFXMode) {
            this.padFXMode = json.padFXMode;
        }

        this.name = json.name;
    }

    deletePad() {
        this.subSequence.deletePad(this);
        // look for sends that have this effectRack
        if (this.sequencerEngine.sends.sends) {
            let sendsToDelete = this.sequencerEngine.sends.sends.filter(
                s => s.pad === this);
            this.sequencerEngine.sends.deleteSends(sendsToDelete);
        }
    }

    sendPadMessages(time) {
        let x = [];
        let notes = [];
        let triggers = this.state.tracks.triggers;
        for (let i=0; i < this.slots.length; i++) {
            let slot = this.slots[i];
            if (!slot) {
                x.push(0);
                notes.push([]);
                continue;
            }

            let trackNotes = [];

            let trackTriggers = triggers[slot.trackNumber];
            if (trackTriggers) {
                for (let track in trackTriggers) {
                    if (trackTriggers[track]) {
                        let transposes = slot.currentStepDatas ?
                            slot.currentStepDatas.map(x => x.transpose) : [];
                        trackNotes = transposes;
                        x.push(1);
                        continue;
                    }
                }
            }


            let playing = (slot.notesTriggered && Object.keys(slot.notesTriggered).length > 0);
            if (playing) {
                if (slot.isFrozen) {
                    x.push(1);
                    notes.push(trackNotes);
                    continue;
                }
                let duration = slot.lastPlayedStepData ? slot.lastPlayedStepData.duration : 1; 
                let velocity = slot.lastPlayedStepData ? slot.lastPlayedStepData.velocity : 1; 
                trackNotes = slot.currentStepDatas ?
                    slot.currentStepDatas.map(x => x.transpose) : [];
                //let transpose = Math.abs(slot.lastPlayedStepData ? slot.lastPlayedStepData.transpose : 0);
                x.push(velocity*(1+Math.max(... Object.values(slot.notesTriggered)) / duration));

            } else {
                x.push(0);
            }
            notes.push(trackNotes);
        }

        if (!time) {
            this.sendMessage("pads playing", x);
            this.sendMessage("notes playing", notes);
            console.log("sending notes playing");
        } else {
            setTimeout(() => {
                this.sendMessage("pads playing", x);
                this.sendMessage("notes playing", notes);
            }, (time - Tone.Master.context.currentTime)*1000 - ANIMATION_LOOKAHEAD);
        }
    }

    onSizeChange(width, height) {
        this.sendMessage("size", [width, height]);
    }

    sendTags() {
        let tags = [];
        for (let slot of this.slots) {
            if (!slot) {
                tags.push('');
            } else {
                let sample = slot.sample;
                tags.push(getTrackShortName(slot, sample));
            }
        }
        this.sendMessage("track types", tags);
    }

    mouseDown(padNumber, flag) {
        let x = new Array(this.slots.length).fill(0);
        x[padNumber] = flag ? 1 : 0;
        this.sendMessage("mouse downs", x);
    }

    sendSliderLevels() {
        let x = [];
        for (let track of this.slots) {
            if (!track) {
                x.push(0);
            } else {
                let volume = track.volumeParameter.get(true);
                x.push(
                    volume < MIN_VOLUME ?
                        0 :
                        Math.pow((volume - MIN_VOLUME)/(MAX_VOLUME-MIN_VOLUME), window.ROOTY));
            }
        }

        this.sendMessage("slider levels", x);
    }

    onSliderValues([values]) {
        for (let i=0; i < values.length; i++) {
            let val = values[i];
            let slot = this.slots[i];
            if (slot) {
                let vol = getVolume(val, MIN_VOLUME, MAX_VOLUME, 1);
                if (slot.volumeParameter.get(true) !== vol) {
                    slot.setVolume(vol, false, false);
                }
            }
        }
    }

    onAddEffectMessage(effect) {
        let chain = this.effectRack;
        let existing = chain.effects.find(x => x.name === effect.name);

        chain.isCollapsed = true;

        if (existing) {
            this.sendMessage("effect added", existing);
        } else {
            this.addRackEffect(effect);
            this.sendMessage("effect added", effect);
        }
        this.dispatch(
            setEffectCollapsed(this.effectRack.id, true));
    }
}

export function getVolume(x, min=-40, max=6, height=1) {
    x = Math.min(Math.max(0, x), height);
    let ratio = x / height;
    let fullRatio = Math.pow(ratio, 1/window.ROOTY);
    return min + (max - min) * fullRatio;
}


export default Pad;
